import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DoubtStatusBadge, DoubtVisibilityBadge } from "@/lib/doubts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle2, Trash2, Send, GraduationCap, User } from "lucide-react";

// Roles allowed to see the back-to-queue link at /faculty vs /student vs /admin.
const HOME_LIST = { ADMIN: "/admin/doubts", FACULTY: "/admin/doubts", STUDENT: "/student/doubts" };

function ReplyBubble({ reply }) {
    const isStaff = reply.author_role === "FACULTY" || reply.author_role === "ADMIN";
    return (
        <div className={`p-4 rounded-xl border ${isStaff ? "border-[#726AF0]/30 bg-[#F0EFFF] dark:bg-[#1C1A2E]" : "border-border bg-muted/40"}`} data-testid={`doubt-reply-${reply.id}`}>
            <div className="flex items-center gap-2 mb-1.5">
                {isStaff ? <GraduationCap className="h-3.5 w-3.5 text-[#726AF0]" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                <span className="text-sm font-semibold">{reply.author_name || "Unknown"}</span>
                <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{reply.author_role}</Badge>
                <span className="text-xs text-muted-foreground ml-auto">{new Date(reply.created_at).toLocaleString()}</span>
            </div>
            <div className="text-sm whitespace-pre-wrap">{reply.body}</div>
        </div>
    );
}

export default function DoubtThread() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [doubt, setDoubt] = useState(null);
    const [replyBody, setReplyBody] = useState("");
    const [sending, setSending] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/doubts/${id}`);
            setDoubt(data);
        } catch (err) {
            toast.error(err.response?.status === 403 ? "You don't have access to this doubt" : "Doubt not found");
            navigate(HOME_LIST[user?.role] || "/");
        }
    }, [id, navigate, user?.role]);
    useEffect(() => { load(); }, [load]);

    if (!doubt) return <div className="text-muted-foreground">Loading…</div>;

    // Mirrors the backend's assert_doubt_write_access: staff (whose read
    // already required batch membership) can always write; a student only on
    // their own doubt.
    const canWrite = user.role !== "STUDENT" || doubt.student_id === user.id;

    const sendReply = async () => {
        if (!replyBody.trim()) return;
        setSending(true);
        try {
            await api.post(`/doubts/${id}/replies`, { body: replyBody.trim() });
            setReplyBody("");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send your reply");
        } finally {
            setSending(false);
        }
    };

    const resolve = async () => {
        try {
            await api.post(`/doubts/${id}/resolve`);
            toast.success("Marked resolved");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not resolve");
        }
    };

    const confirmDelete = async () => {
        if (!deleteReason.trim()) return;
        try {
            await api.post(`/doubts/${id}/delete`, { reason: deleteReason.trim() });
            toast.success("Doubt deleted");
            navigate(HOME_LIST[user.role] || "/");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not delete");
        }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="doubt-thread-page">
            <Button variant="ghost" size="sm" onClick={() => navigate(HOME_LIST[user.role] || "/")} data-testid="doubt-thread-back">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back to doubts
            </Button>

            <Card className="mx-card">
                <CardContent className="p-6 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <DoubtStatusBadge status={doubt.status} />
                        <DoubtVisibilityBadge visibility={doubt.visibility} />
                        <Badge variant="outline" className="text-[10px]">{doubt.course_title}</Badge>
                    </div>
                    <h1 className="font-display text-2xl lg:text-3xl">{doubt.title}</h1>
                    <div className="text-sm text-muted-foreground">
                        Asked by <strong className="text-foreground">{doubt.student_name}</strong> · {new Date(doubt.created_at).toLocaleString()}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{doubt.body}</p>

                    {canWrite && (
                        <div className="flex flex-wrap gap-2 pt-2">
                            {doubt.status !== "resolved" && (
                                <Button variant="outline" className="rounded-xl" onClick={resolve} data-testid="doubt-resolve-btn">
                                    <CheckCircle2 className="h-4 w-4 mr-2" /> Mark resolved
                                </Button>
                            )}
                            <Button variant="ghost" className="rounded-xl text-[#E11D48] hover:text-[#E11D48]"
                                onClick={() => setDeleteOpen(true)} data-testid="doubt-delete-btn">
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="space-y-3">
                <div className="text-sm font-semibold">{doubt.replies.length} {doubt.replies.length === 1 ? "reply" : "replies"}</div>
                {doubt.replies.map(r => <ReplyBubble key={r.id} reply={r} />)}
                {doubt.replies.length === 0 && (
                    <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-xl">
                        No replies yet.
                    </div>
                )}
            </div>

            {canWrite ? (
                <Card className="mx-card">
                    <CardContent className="p-5 space-y-3">
                        <Label>{user.role === "STUDENT" ? "Add a follow-up" : "Write a reply"}</Label>
                        <Textarea rows={3} value={replyBody} onChange={e => setReplyBody(e.target.value)}
                            placeholder={user.role === "STUDENT" ? "Still stuck? Add more detail…" : "Explain the answer…"}
                            data-testid="doubt-reply-input" />
                        <div className="flex justify-end">
                            <Button onClick={sendReply} disabled={!replyBody.trim() || sending} className="mx-btn-primary" data-testid="doubt-reply-submit">
                                <Send className="h-4 w-4 mr-2" /> {sending ? "Sending…" : "Send"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="text-xs text-muted-foreground text-center">
                    Only {doubt.student_name} or staff can reply to this doubt.
                </div>
            )}

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete this doubt?</DialogTitle>
                        <DialogDescription>It's never hard-deleted — it's just hidden from every list. A reason is required.</DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label>Reason</Label>
                        <Textarea rows={3} value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
                            placeholder="e.g. duplicate, resolved in class, posted by mistake" data-testid="doubt-delete-reason-input" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-xl" onClick={() => setDeleteOpen(false)}>Keep it</Button>
                        <Button onClick={confirmDelete} disabled={!deleteReason.trim()} className="bg-[#E11D48] hover:bg-[#E11D48] text-white rounded-xl" data-testid="doubt-confirm-delete-btn">
                            Delete it
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
